import React, { useState, useEffect, useContext } from "react";
import { AuthContext } from "../App";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import ChangePassword from "../components/ChangePassword";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { User, BookOpen, Target, Briefcase, Award, Save, MapPin, DollarSign, Camera, Upload, Edit2, Mail, Phone, UserCircle } from "lucide-react";
import { useLanguage } from "../contexts/LanguageContext";
import { LoadingPage } from "@/components/ui/loading-spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Profile = () => {
  const { token, user, updateUser } = useContext(AuthContext);
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [countries, setCountries] = useState([]);
  const [currencies, setCurrencies] = useState([]);
  const [profileImage, setProfileImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  const [formData, setFormData] = useState({
    education: "",
    interested_subjects: [],
    career_ambitions: "",
    current_field: "",
    work_experience: "",
    preferred_career_field: "",
    city: "",
    state: "",
    zip_code: "",
    country: "",
    preferred_currency: "USD"
  });

  const [subjectsInput, setSubjectsInput] = useState("");

  useEffect(() => {
    const fetchLocationData = async () => {
      try {
        const [countriesRes, currenciesRes] = await Promise.all([
          axios.get(`${API}/countries`),
          axios.get(`${API}/currencies`)
        ]);
        setCountries(countriesRes.data.countries || []);
        setCurrencies(currenciesRes.data.currencies || []);
      } catch (error) {
        console.error("Failed to load countries/currencies", error);
      }
    };
    fetchLocationData();
  }, []);

  useEffect(() => {
    if (!user) {
      navigate("/");
      return;
    }
    // Pre-fill form with existing user data
    setFormData({
      education: user.education || "",
      interested_subjects: user.interested_subjects || [],
      career_ambitions: user.career_ambitions || "",
      current_field: user.current_field || "",
      work_experience: user.work_experience || "",
      preferred_career_field: user.preferred_career_field || "",
      city: user.city || "",
      state: user.state || "",
      zip_code: user.zip_code || "",
      country: user.country || "",
      preferred_currency: user.preferred_currency || "USD"
    });
    setSubjectsInput(user.interested_subjects?.join(", ") || "");
    setImagePreview(user.profile_image || null);
  }, [user]);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      
      // Check file type
      if (!file.type.startsWith('image/')) {
        toast.error("Please select an image file");
        return;
      }
      
      setProfileImage(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!profileImage) return;
    
    try {
      setUploadingImage(true);
      
      const formData = new FormData();
      formData.append('profile_image', profileImage);
      
      const response = await axios.post(`${API}/profile/upload-image`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      updateUser({ ...user, profile_image: response.data.profile_image });
      toast.success("Profile image updated successfully!");
      setProfileImage(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload image");
      console.error(error);
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      
      // Convert comma-separated subjects to array
      const subjects = subjectsInput.split(',').map(s => s.trim()).filter(s => s);
      
      const updates = {
        ...formData,
        interested_subjects: subjects
      };

      const response = await axios.patch(`${API}/profile`, updates, {
        headers: { Authorization: `Bearer ${token}` }
      });

      updateUser(response.data);
      toast.success("Profile updated successfully!");
      setEditing(false);
    } catch (error) {
      toast.error("Failed to update profile");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
        <Navbar />
        <LoadingPage message="Loading profile..." />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-4 py-6 md:py-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-primary to-primary/80 flex items-center justify-center shadow-md">
                <UserCircle className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl md:text-4xl font-bold text-gray-900">
                  {t('profile.title')}
                </h1>
                <p className="text-gray-600 text-sm md:text-base">
                  {t('profile.manageInfo')}
                </p>
              </div>
            </div>
            {!editing && (
              <Button 
                onClick={() => setEditing(true)}
                className="shadow-md hover:shadow-lg transition-shadow w-full md:w-auto"
                size="lg"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                {t('profile.editProfile')}
              </Button>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Profile Image & Quick Stats */}
          <div className="lg:col-span-1 space-y-6">
            {/* Profile Image Card */}
            <Card className="border-none shadow-md">
              <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
              <CardContent className="pt-6">
                <div className="flex flex-col items-center">
                  {/* Image Preview */}
                  <div className="relative mb-4">
                    <div className="w-40 h-40 rounded-full overflow-hidden bg-gradient-to-br from-primary/10 to-primary/5 border-4 border-white shadow-xl">
                      {imagePreview ? (
                        <img 
                          src={imagePreview} 
                          alt="Profile" 
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <User className="w-20 h-20 text-primary/30" />
                        </div>
                      )}
                    </div>
                    {editing && (
                      <label 
                        htmlFor="profile-image" 
                        className="absolute bottom-2 right-2 w-12 h-12 bg-primary rounded-full flex items-center justify-center cursor-pointer hover:bg-primary/90 transition-colors shadow-lg"
                      >
                        <Camera className="w-5 h-5 text-white" />
                        <input
                          id="profile-image"
                          type="file"
                          accept="image/*"
                          onChange={handleImageChange}
                          className="hidden"
                        />
                      </label>
                    )}
                  </div>

                  {/* User Name & Role */}
                  <div className="text-center mb-4">
                    <h2 className="text-2xl font-bold text-gray-900 mb-1">{user.name}</h2>
                    <Badge variant="outline" className="bg-primary/10 text-primary border-primary/20">
                      {user.user_type?.replace('_', ' ').toUpperCase()}
                    </Badge>
                  </div>

                  {/* Upload Actions */}
                  {editing && (
                    <div className="w-full space-y-3">
                      <div className="text-xs text-muted-foreground bg-muted/30 p-3 rounded-lg">
                        <p className="mb-1">• Size: 400x400px</p>
                        <p className="mb-1">• Format: JPG, PNG</p>
                        <p>• Max: 5MB</p>
                      </div>
                      {profileImage && (
                        <Button 
                          onClick={handleUploadImage} 
                          disabled={uploadingImage}
                          className="w-full shadow-md"
                          size="sm"
                        >
                          <Upload className="w-4 h-4 mr-2" />
                          {uploadingImage ? "Uploading..." : "Upload Image"}
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Stats Card */}
            <Card className="border-none shadow-md">
              <div className="h-2 bg-gradient-to-r from-success via-success/80 to-success/60"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Award className="w-5 h-5 text-success" />
                  Your Stats
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex items-center justify-between p-4 bg-primary/5 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Points</p>
                      <p className="text-2xl font-bold text-primary">{user.points || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Award className="w-6 h-6 text-primary" />
                    </div>
                  </div>
                  <div className="flex items-center justify-between p-4 bg-success/5 rounded-lg">
                    <div>
                      <p className="text-sm text-muted-foreground">Badges</p>
                      <p className="text-2xl font-bold text-success">{user.badges?.length || 0}</p>
                    </div>
                    <div className="w-12 h-12 rounded-lg bg-success/10 flex items-center justify-center">
                      <Target className="w-6 h-6 text-success" />
                    </div>
                  </div>
                  <div className="p-4 bg-accent/5 rounded-lg text-center">
                    <p className="text-sm text-muted-foreground mb-2">Subscription Plan</p>
                    <Badge variant="outline" className="text-base px-4 py-1 bg-accent/10 text-accent border-accent/20">
                      {user.subscription_tier || 'FREE'}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right Column - Profile Details */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Card */}
            <Card className="border-none shadow-md">
              <div className="h-2 bg-gradient-to-r from-accent via-accent/80 to-accent/60"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5 text-accent" />
                  {t('profile.basicInfo')}
                </CardTitle>
                <CardDescription>Your account information</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="p-4 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Mail className="w-4 h-4 text-primary" />
                      <Label className="text-sm text-muted-foreground">{t('auth.emailLabel')}</Label>
                    </div>
                    <p className="font-medium text-foreground">{user.email}</p>
                  </div>
                  <div className="p-4 bg-muted/20 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <Phone className="w-4 h-4 text-primary" />
                      <Label className="text-sm text-muted-foreground">Mobile Number</Label>
                    </div>
                    <p className="font-medium text-foreground">{user.mobile || 'Not provided'}</p>
                  </div>
                  {user.grade && (
                    <div className="p-4 bg-muted/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4 text-primary" />
                        <Label className="text-sm text-muted-foreground">{t('dashboard.gradeLabel')}</Label>
                      </div>
                      <p className="font-medium text-foreground">{t('dashboard.gradeLabel')} {user.grade}</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Profile Details Card */}
            <Card className="border-none shadow-md">
              <div className="h-2 bg-gradient-to-r from-primary via-primary/80 to-primary/60"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-primary" />
                  {t('profile.profileDetails')}
                </CardTitle>
                <CardDescription>
                  {editing ? t('profile.updateInfo') : t('profile.careerEducationalInfo')}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div>
                      <Label htmlFor="education" className="text-sm font-medium">{t('profile.education')}</Label>
                      <Input
                        id="education"
                        value={formData.education}
                        onChange={(e) => setFormData({...formData, education: e.target.value})}
                        placeholder={t('profile.educationPlaceholder')}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="subjects" className="text-sm font-medium">{t('profile.interests')}</Label>
                      <Input
                        id="subjects"
                        value={subjectsInput}
                        onChange={(e) => setSubjectsInput(e.target.value)}
                        placeholder={t('profile.interestsPlaceholder')}
                        className="mt-1.5"
                      />
                      <p className="text-xs text-muted-foreground mt-1">Separate multiple interests with commas</p>
                    </div>

                    {user.user_type === 'college_student' && (
                      <div>
                        <Label htmlFor="current_field" className="text-sm font-medium">Current Field/Major</Label>
                        <Input
                          id="current_field"
                          value={formData.current_field}
                          onChange={(e) => setFormData({...formData, current_field: e.target.value})}
                          placeholder="e.g., Computer Science, Business Administration"
                          className="mt-1.5"
                        />
                      </div>
                    )}

                    {user.user_type === 'job_seeker' && (
                      <div>
                        <Label htmlFor="work_experience" className="text-sm font-medium">Work Experience</Label>
                        <Textarea
                          id="work_experience"
                          value={formData.work_experience}
                          onChange={(e) => setFormData({...formData, work_experience: e.target.value})}
                          placeholder="Describe your work experience..."
                          rows={3}
                          className="mt-1.5"
                        />
                      </div>
                    )}

                    <div>
                      <Label htmlFor="ambitions" className="text-sm font-medium">Career Ambitions</Label>
                      <Textarea
                        id="ambitions"
                        value={formData.career_ambitions}
                        onChange={(e) => setFormData({...formData, career_ambitions: e.target.value})}
                        placeholder="What are your career goals and ambitions?"
                        rows={3}
                        className="mt-1.5"
                      />
                    </div>

                    <div>
                      <Label htmlFor="preferred_field" className="text-sm font-medium">Preferred Career Field</Label>
                      <Input
                        id="preferred_field"
                        value={formData.preferred_career_field}
                        onChange={(e) => setFormData({...formData, preferred_career_field: e.target.value})}
                        placeholder="e.g., Software Engineering, Medicine, Finance"
                        className="mt-1.5"
                      />
                    </div>
                  </>
                ) : (
                  <div className="space-y-4">
                    <div className="p-4 bg-muted/20 rounded-lg">
                      <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                        <BookOpen className="w-4 h-4" />
                        Education
                      </Label>
                      <p className="font-medium">{formData.education || "Not specified"}</p>
                    </div>

                    <div className="p-4 bg-muted/20 rounded-lg">
                      <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4" />
                        Interested Subjects
                      </Label>
                      <div className="flex flex-wrap gap-2">
                        {formData.interested_subjects?.length > 0 ? (
                          formData.interested_subjects.map((subject, idx) => (
                            <Badge key={idx} variant="secondary" className="text-sm">
                              {subject}
                            </Badge>
                          ))
                        ) : (
                          <p className="text-muted-foreground text-sm">Not specified</p>
                        )}
                      </div>
                    </div>

                    {user.user_type === 'college_student' && formData.current_field && (
                      <div className="p-4 bg-muted/20 rounded-lg">
                        <Label className="text-sm text-muted-foreground mb-2 block">Current Field/Major</Label>
                        <p className="font-medium">{formData.current_field}</p>
                      </div>
                    )}

                    {user.user_type === 'job_seeker' && formData.work_experience && (
                      <div className="p-4 bg-muted/20 rounded-lg">
                        <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                          <Briefcase className="w-4 h-4" />
                          Work Experience
                        </Label>
                        <p className="font-medium whitespace-pre-line">{formData.work_experience}</p>
                      </div>
                    )}

                    <div className="p-4 bg-muted/20 rounded-lg">
                      <Label className="text-sm text-muted-foreground flex items-center gap-2 mb-2">
                        <Target className="w-4 h-4" />
                        Career Ambitions
                      </Label>
                      <p className="font-medium whitespace-pre-line">{formData.career_ambitions || "Not specified"}</p>
                    </div>

                    <div className="p-4 bg-muted/20 rounded-lg">
                      <Label className="text-sm text-muted-foreground mb-2 block">Preferred Career Field</Label>
                      <p className="font-medium">{formData.preferred_career_field || "Not specified"}</p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Location & Currency Card */}
            <Card className="border-none shadow-md">
              <div className="h-2 bg-gradient-to-r from-success via-success/80 to-success/60"></div>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-success" />
                  Location & Currency
                </CardTitle>
                <CardDescription>
                  {editing ? "Update your location and preferred currency" : "Your location and currency preferences"}
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {editing ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="city" className="text-sm font-medium">City</Label>
                        <Input
                          id="city"
                          value={formData.city}
                          onChange={(e) => setFormData({...formData, city: e.target.value})}
                          placeholder="e.g., Mumbai"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="state" className="text-sm font-medium">State/Province</Label>
                        <Input
                          id="state"
                          value={formData.state}
                          onChange={(e) => setFormData({...formData, state: e.target.value})}
                          placeholder="e.g., Maharashtra"
                          className="mt-1.5"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="zip_code" className="text-sm font-medium">ZIP/Postal Code</Label>
                        <Input
                          id="zip_code"
                          value={formData.zip_code}
                          onChange={(e) => setFormData({...formData, zip_code: e.target.value})}
                          placeholder="e.g., 400001"
                          className="mt-1.5"
                        />
                      </div>
                      <div>
                        <Label htmlFor="country" className="text-sm font-medium">Country</Label>
                        <Select
                          value={formData.country}
                          onValueChange={(value) => setFormData({...formData, country: value})}
                        >
                          <SelectTrigger id="country" className="mt-1.5">
                            <SelectValue placeholder="Select country" />
                          </SelectTrigger>
                          <SelectContent className="max-h-60">
                            {countries.map((country) => (
                              <SelectItem key={country.code} value={country.code}>
                                {country.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="preferred_currency" className="text-sm font-medium">Preferred Currency</Label>
                      <Select
                        value={formData.preferred_currency}
                        onValueChange={(value) => setFormData({...formData, preferred_currency: value})}
                      >
                        <SelectTrigger id="preferred_currency" className="mt-1.5">
                          <SelectValue placeholder="Select currency" />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          {currencies.map((currency) => (
                            <SelectItem key={currency.code} value={currency.code}>
                              {currency.symbol} {currency.code} - {currency.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-2">Used for subscription pricing and payments</p>
                    </div>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="flex items-start gap-3 p-4 bg-muted/20 rounded-lg">
                      <MapPin className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <Label className="text-sm text-muted-foreground mb-1 block">Location</Label>
                        <p className="font-medium">
                          {[formData.city, formData.state, formData.zip_code, countries.find(c => c.code === formData.country)?.name]
                            .filter(Boolean)
                            .join(", ") || "Not specified"}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 p-4 bg-muted/20 rounded-lg">
                      <DollarSign className="w-5 h-5 text-success mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <Label className="text-sm text-muted-foreground mb-1 block">Preferred Currency</Label>
                        <p className="font-medium">
                          {currencies.find(c => c.code === formData.preferred_currency)?.name || formData.preferred_currency}
                          {" "}
                          ({currencies.find(c => c.code === formData.preferred_currency)?.symbol || "$"})
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Change Password Section */}
            <ChangePassword />

            {/* Action Buttons */}
            {editing && (
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={handleSave} 
                  disabled={loading}
                  className="shadow-md hover:shadow-lg transition-shadow flex-1"
                  size="lg"
                >
                  <Save className="w-4 h-4 mr-2" />
                  {loading ? "Saving..." : "Save Changes"}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditing(false)}
                  size="lg"
                  className="flex-1 sm:flex-none"
                >
                  Cancel
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default Profile;
